import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acAPI } from "../../lib/academicApi";
import { PageHeader, KPITile, Section, Loading, AIInsightCard, EmptyState, StatusPill } from "../../components/Common";
import { toast } from "sonner";
import {
  GraduationCap, TrendingUp, AlertTriangle, Sparkles, FileText,
  BookOpen, ClipboardCheck, Users, ChevronRight, Award, Layers, RefreshCw,
} from "lucide-react";
import { downloadAuthedPdf } from "../../lib/admApi";

export default function AcademicHub() {
  const { school } = useParams();
  const nav = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [atRisk, setAtRisk] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [k, r] = await Promise.all([acAPI.kpis(), acAPI.atRisk(45)]);
    setKpis(k.data);
    setAtRisk(r.data);
  };
  useEffect(() => { load(); }, []);

  const gen = async (fn, label) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setBriefing(data);
      toast.success(label + " generated (advisory)");
    } catch { toast.error("AI unavailable"); }
    finally { setBusy(false); }
  };

  const dl = async () => {
    try { await downloadAuthedPdf(acAPI.executivePdf(), "executive-academic.pdf"); }
    catch (e) { toast.error(e.message); }
  };

  if (!kpis) return <Loading label="Loading academic intelligence…" />;

  return (
    <div data-testid="academic-hub">
      <PageHeader
        kicker="Academic360™ · Executive Academic Intelligence"
        title="School academic health"
        subtitle="Cambridge · IB · CAPS · ZIMSEC · Custom. Board-ready view of curriculum, marks and interventions."
        actions={
          <div className="flex gap-2">
            <button onClick={() => nav(`/education360/${school}/academic/workbench`)} className="hl-button-secondary text-xs" data-testid="btn-teacher-workbench">Teacher Workbench</button>
            <button onClick={() => nav(`/education360/${school}/academic/markbooks`)} className="hl-button-secondary text-xs" data-testid="btn-markbooks">Mark Books</button>
            <button onClick={() => nav(`/education360/${school}/academic/promotion`)} className="hl-button-secondary text-xs" data-testid="btn-promotion">Promotion</button>
            <button onClick={load} className="hl-button-secondary text-xs"><RefreshCw className="w-3 h-3" /></button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <KPITile testId="kpi-health" label="School Health" value={kpis.school_health == null ? "N/A" : `${kpis.school_health}/100`} tone="dark" />
        <KPITile testId="kpi-avg" label="Average score" value={kpis.avg_score == null ? "N/A" : `${kpis.avg_score}%`} />
        <KPITile testId="kpi-pass" label="Pass rate" value={kpis.pass_rate == null ? "N/A" : `${kpis.pass_rate}%`} accent="Live" />
        <KPITile testId="kpi-dist" label="Distinction rate" value={kpis.distinction_rate == null ? "N/A" : `${kpis.distinction_rate}%`} />
        <KPITile testId="kpi-atrisk" label="At-risk students" value={kpis.at_risk_count} sub="advisory watchlist" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Section title="Departments" subtitle="Performance across subject departments" testId="dept-panel">
          <div className="hl-card p-5 space-y-2">
            {kpis.departments.length === 0 && <div className="text-sm text-slate-500">No department data yet.</div>}
            {kpis.departments.map(d => (
              <div key={d.name} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100">
                <div className="text-slate-700">{d.name}</div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-slate-500">{d.subjects} subjects</span>
                  <span className="font-mono text-slate-700">{d.avg_score}%</span>
                  <span className="text-emerald-700 font-semibold">{d.pass_rate}% pass</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Top subjects" subtitle="Ranked by average" testId="subjects-panel">
          <div className="hl-card p-5 space-y-1.5">
            {kpis.subjects.slice(0, 10).map((s, i) => (
              <div key={s.subject_id} className="flex items-center gap-3 text-sm py-1">
                <div className="w-4 text-xs text-slate-400 font-mono">{i + 1}</div>
                <div className="flex-1 truncate text-slate-700">{s.name}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[color:var(--hl-navy)]" style={{ width: `${s.avg_score}%` }} />
                </div>
                <div className="text-xs font-mono w-12 text-right">{s.avg_score}%</div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="At-risk watchlist" subtitle="Advisory intervention list" testId="atrisk-panel">
          <div className="hl-card p-5 max-h-[380px] overflow-y-auto space-y-2">
            {atRisk.length === 0 && <div className="text-sm text-slate-500">No students below threshold.</div>}
            {atRisk.slice(0, 10).map(s => (
              <div key={s.student_id} className="border-l-2 border-l-amber-400 pl-3 py-1 text-sm">
                <div className="font-semibold text-slate-800">{s.name} <span className="text-xs text-slate-500">· {s.year_group}</span></div>
                <div className="text-xs text-slate-500">
                  {s.risk_subjects.map(r => `${r.subject} ${r.grade}(${r.score}%)`).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section
        title="Executive Academic Intelligence"
        subtitle="Advisory only — final decisions rest with the school."
        actions={
          <div className="flex gap-2">
            <button className="hl-button-secondary text-xs disabled:opacity-50" disabled={busy} onClick={() => gen(acAPI.aiAtRisk, "At-risk advisory")} data-testid="btn-ai-atrisk">
              <AlertTriangle className="w-3 h-3 inline mr-1" />At-risk advisory
            </button>
            <button className="hl-button-primary text-xs disabled:opacity-50" disabled={busy} onClick={() => gen(acAPI.aiExec, "Executive briefing")} data-testid="btn-ai-exec">
              <Sparkles className="w-3 h-3 inline mr-1" />{busy ? "Generating…" : "Executive briefing"}
            </button>
            <button className="hl-button-secondary text-xs" onClick={dl} data-testid="btn-dl-exec">
              <FileText className="w-3 h-3 inline mr-1" />Download PDF
            </button>
          </div>
        }
      >
        {briefing ? (
          <AIInsightCard testId="ai-briefing" title={briefing.title} body={briefing.body} severity="advisory" />
        ) : (
          <EmptyState icon={Sparkles} title="No briefing yet" body="Generate a board-ready executive academic briefing." testId="no-briefing" />
        )}
      </Section>

      <Section title="Quick actions">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-actions">
          <ActionCard label="Curriculum Studio" icon={Layers} onClick={() => nav(`/education360/${school}/academic/curriculum`)} testId="qa-curriculum" />
          <ActionCard label="Homework360" icon={BookOpen} onClick={() => nav(`/education360/${school}/academic/homework`)} testId="qa-homework" />
          <ActionCard label="Moderation360" icon={ClipboardCheck} onClick={() => nav(`/education360/${school}/academic/moderation`)} testId="qa-moderation" />
          <ActionCard label="Grade Books" icon={Award} onClick={() => nav(`/education360/${school}/academic/gradebook`)} testId="qa-gradebook" />
        </div>
      </Section>
    </div>
  );
}

function ActionCard({ label, icon: Icon, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="hl-card p-5 text-left flex items-center gap-3 hover:border-[color:var(--hl-orange)] transition-colors">
      <div className="w-10 h-10 bg-[color:var(--hl-navy)] text-white rounded grid place-items-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="font-semibold text-slate-800">{label}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </button>
  );
}
